import React, { useEffect, useId, useRef, useState } from "react";
import {
  useSpeakerRecordSearch,
  useTranscriptSpeakers,
} from "../Data/interactions";
import type { SpeakerCandidate } from "../Dashboard/api";
import type { ShallowSearchResult } from "../Dashboard/types";
import { useDebouncedValue } from "@/hooks/productTimerHooks";
import { hasSoftKeyboard } from "@/utils/softKeyboard";

export interface SpeakerRenamePickerProps {
  interactionId: string;
  /** The rename landed. */
  onClose: () => void;
  /** Rename every line of the speaker; `userId` when the name is a colleague,
   *  `entityId` when it is a person record (#14412). */
  onRename: (name: string, userId?: string, entityId?: string) => Promise<void>;
}

/** How long typing pauses before the person records are searched (#14412). */
export const RECORD_SEARCH_DEBOUNCE_MS = 200;
/** The fewest typed characters the person records are searched for. */
const RECORD_SEARCH_MIN_CHARS = 2;
/** The most person records offered under the fixed list. */
const RECORDS_OFFERED = 5;

/** A person record of the organization the speaker could be (#14412). */
interface SpeakerRecord {
  id: string;
  name: string;
  /** The person's role as the organization keeps it, or null. */
  role: string | null;
}

/** The person's role under the name, as the People card joins it: the
 *  title and the employer, either alone, or null when neither is kept. */
const roleLine = (
  title: string | null | undefined,
  employer: string | null | undefined
): string | null => [title, employer].filter(Boolean).join(" · ") || null;

const matches = (person: SpeakerCandidate, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    person.name.toLowerCase().includes(q) ||
    (person.email ?? "").toLowerCase().includes(q)
  );
};

const sameName = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Who a transcript speaker is (#11884): the meeting's attendees and the
 * organization's people, filtered as the name is typed, then the
 * organization's person records that match what is typed (#14412), or the
 * typed name itself on Enter. Picking renames every line the speaker has; a
 * record is also tagged as a participant of the meeting. It sits beside the
 * transcript rather than over it, and stays while the reader goes through
 * the speaker's lines; the transcript owns closing it (#12489).
 */
const SpeakerRenamePicker: React.FC<SpeakerRenamePickerProps> = ({
  interactionId,
  onClose,
  onRename,
}) => {
  const listId = useId();
  // The field takes the focus on open, as it always has, but does not scroll
  // the panel to it (#13917): where the panel docks short, the section above
  // the field is what the reader opened it for.
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!hasSoftKeyboard()) field.current?.focus({ preventScroll: true });
  }, []);
  const { data, isPending } = useTranscriptSpeakers(interactionId, true);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const typed = query.trim();
  const searched = useDebouncedValue(typed, RECORD_SEARCH_DEBOUNCE_MS);
  const recordSearch = useSpeakerRecordSearch(
    searched,
    searched.length >= RECORD_SEARCH_MIN_CHARS
  );
  // The last answer a search gave, kept here rather than in the query: it
  // stays while a newer search runs and when one fails. A late answer for an
  // older query lands under its own key and never reaches it.
  const answered = recordSearch.isSuccess ? recordSearch.data : undefined;
  const [lastAnswer, setLastAnswer] = useState<ShallowSearchResult[]>([]);
  useEffect(() => {
    if (answered) setLastAnswer(answered.results);
  }, [answered]);
  const fixed = data?.people ?? [];
  const people = fixed.filter((p) => matches(p, query));
  // The search's matches as it ranks them, an alias match included; the last
  // answer's rows stay as they are until the next answer lands.
  const records: SpeakerRecord[] =
    typed.length >= RECORD_SEARCH_MIN_CHARS
      ? lastAnswer
          .map((r) => r.entity)
          // Someone the fixed list already offers is not offered twice.
          .filter((e) => !fixed.some((p) => sameName(p.name, e.name)))
          .slice(0, RECORDS_OFFERED)
          .map((e) => ({
            id: e.id,
            name: e.name,
            role: roleLine(e.role_title, e.employer),
          }))
      : [];
  const typedIsNew =
    typed !== "" &&
    !fixed.some((p) => sameName(p.name, typed)) &&
    !records.some((r) => sameName(r.name, typed));

  const choose = async (name: string, userId?: string, entityId?: string) => {
    setSaving(true);
    setError(null);
    try {
      await onRename(name, userId, entityId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const submitTyped = () => {
    if (!typed || saving) return;
    const exact = fixed.find((p) => sameName(p.name, typed));
    if (exact) {
      void choose(exact.name, exact.user_id ?? undefined);
      return;
    }
    const sameRecords = records.filter((r) => sameName(r.name, typed));
    if (sameRecords.length === 1) {
      void choose(sameRecords[0].name, undefined, sameRecords[0].id);
      return;
    }
    void choose(typed);
  };

  return (
    <div>
      <input
        role="combobox"
        aria-label="Speaker name"
        aria-expanded
        aria-controls={listId}
        ref={field}
        value={query}
        disabled={saving}
        placeholder="Name"
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submitTyped();
          }
        }}
        className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-[#10141B] focus:border-gray-400 focus:outline-none"
      />
      <ul
        id={listId}
        role="listbox"
        aria-label="People"
        className="mt-2 max-h-64 overflow-y-auto"
      >
        {people.map((person) => (
          <li
            key={`${person.user_id ?? ""}|${person.email ?? ""}|${person.name}`}
            role="option"
            aria-selected={false}
            aria-disabled={saving}
            onClick={() =>
              !saving && void choose(person.name, person.user_id ?? undefined)
            }
            className="flex cursor-pointer flex-col rounded-md px-2 py-1.5 hover:bg-gray-100"
          >
            <span className="truncate text-sm text-[#10141B]">
              {person.name}
            </span>
            {(person.enrolled || person.email) && (
              <span className="truncate font-mono text-[11px] text-[#6E7480]">
                {person.enrolled ? "Recognized by voice" : person.email}
              </span>
            )}
          </li>
        ))}
        {records.map((record) => (
          <li
            key={`record|${record.id}`}
            role="option"
            aria-selected={false}
            aria-disabled={saving}
            onClick={() =>
              !saving && void choose(record.name, undefined, record.id)
            }
            className="flex cursor-pointer flex-col rounded-md px-2 py-1.5 hover:bg-gray-100"
          >
            <span className="truncate text-sm text-[#10141B]">
              {record.name}
            </span>
            {record.role && (
              <span className="truncate font-mono text-[11px] text-[#6E7480]">
                {record.role}
              </span>
            )}
          </li>
        ))}
        {!isPending && typedIsNew && (
          <li
            role="option"
            aria-selected={false}
            aria-disabled={saving}
            onClick={submitTyped}
            className="flex cursor-pointer flex-col rounded-md px-2 py-1.5 hover:bg-gray-100"
          >
            <span className="truncate text-sm text-[#10141B]">{typed}</span>
            <span className="font-mono text-[11px] text-[#6E7480]">
              New name
            </span>
          </li>
        )}
      </ul>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default SpeakerRenamePicker;
